import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type TaxRefundMethodVersion } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethod'
import { type TaxRefundMethodStore } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethodStore'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'

/**
 * View model for the delete tax refund method dialog.
 */
export class DeleteTaxRefundMethodDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor taxRefundMethod: TaxRefundMethodVersion | null = null

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _taxRefundMethodStore: TaxRefundMethodStore,
  ) {}

  /**
   * Prompts for and performs deletion if accepted.
   *
   * @param taxRefundMethod
   * @param signal
   * @returns A boolean promise that resolves to true if the
   *  tax refund method was deleted, or false if the operation was cancelled or
   *  the user declined.
   */
  async prompt(taxRefundMethod: TaxRefundMethodVersion, signal?: AbortSignal) {
    // Close any ongoing prompt
    this.modal.close()

    // Start a new prompt
    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.taxRefundMethod = taxRefundMethod
      this.modal.show()
    })

    // Wait for the user to confirm or cancel
    const response = await responsePromise

    // Close the dialog
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  delete = task.resolved(async () => {
    if (!this.taxRefundMethod) {
      return
    }

    await this._taxRefundMethodStore
      .delistTaxRefundMethod(this.taxRefundMethod.id)
      .catch(
        this._createSnackbarErrorHandler(
          'Deleting the tax refund method failed',
        ),
      )

    this._prompt.respond(true)
    this.modal.close()
  })
}
